// Administration › Chantiers : hypothèses par défaut de l'entreprise pour « combien de personnes, combien de jours ».
// AUCUNE valeur n'est préremplie par TRAME : tant que les heures sur site par jour ne sont pas saisies, le tableau des chantiers
// ne s'affiche pas (et l'écran le dit). Chaque chantier peut ensuite avoir ses propres valeurs.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { nombre } from '../../lib/format.ts';
import { Alerte, Bouton, Champ, ChampChoix, Chargement, EtatErreur, Panneau, useNotifier } from '../../components/ui.tsx';

interface Reglages { heures_sur_site_par_jour: string | null; rendement_pct: string | null; lieu_programmation: 'site' | 'bureau' | null }
const aff = (s: string | null) => (s === null ? '' : nombre(s, 4).replace(/ | /g, ''));

export function OngletChantiers() {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const q = useQuery({ queryKey: ['reglages-chantier'], queryFn: () => api.get<{ reglages: Reglages; modifiable: boolean }>('/api/organisation/chantier') });
  const [hj, setHj] = useState('');
  const [rd, setRd] = useState('');
  const [prog, setProg] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => {
    if (!q.data) return;
    setHj(aff(q.data.reglages.heures_sur_site_par_jour)); setRd(aff(q.data.reglages.rendement_pct)); setProg(q.data.reglages.lieu_programmation ?? '');
  }, [q.data]);
  if (q.isLoading) return <Chargement />;
  if (q.isError) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  const enregistrer = async () => {
    setErreur(null); setEnvoi(true);
    try {
      await api.put('/api/organisation/chantier', { heuresSurSiteParJour: hj.trim() || null, rendementPct: rd.trim() || null, lieuProgrammation: prog || null });
      await qc.invalidateQueries({ queryKey: ['reglages-chantier'] });
      await qc.invalidateQueries({ queryKey: ['chantier'] });
      notifier('Hypothèses des chantiers enregistrées.');
    } catch (e) { setErreur(messageErreur(e)); } finally { setEnvoi(false); }
  };
  return (
    <Panneau titre="Chantiers : hypothèses par défaut">
      <div className="pile">
        <p className="secondaire" style={{ margin: 0 }}>Ces valeurs servent à proposer le nombre de personnes et de jours d’un chantier préparé depuis un devis accepté.
          Elles restent modifiables chantier par chantier. TRAME n’en pose aucune à votre place.</p>
        {!q.data!.reglages.heures_sur_site_par_jour && <Alerte ton="ambre" titre="Heures sur site par jour non saisies">Tant qu’elles manquent, le tableau « combien de personnes, combien de jours » ne s’affiche pas.</Alerte>}
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <div className="grille-form">
          <Champ libelle="Heures de travail sur site, par jour et par personne" inputMode="decimal" value={hj} onChange={(e) => setHj(e.target.value)}
            aide="Journée de travail moins les trajets et les pauses (ex. : 7)." disabled={!q.data!.modifiable} />
          <Champ libelle="Rendement (%)" inputMode="decimal" value={rd} onChange={(e) => setRd(e.target.value)}
            aide="Vide = 100 % (aucune correction). À ajuster après quelques chantiers, d’après les heures passées." disabled={!q.data!.modifiable} />
          <ChampChoix libelle="La programmation se fait" value={prog} onChange={(e) => setProg(e.target.value)} vide="Non précisé (comptée sur site)" disabled={!q.data!.modifiable}
            options={[{ valeur: 'bureau', libelle: 'Au bureau' }, { valeur: 'site', libelle: 'Sur site' }]}
            aide="Lieu proposé pour les lignes « programmation » du devis ; modifiable poste par poste." />
        </div>
        {q.data!.modifiable && <div className="barre-actions"><Bouton variante="principal" icone={<Save aria-hidden />} chargement={envoi} onClick={enregistrer}>Enregistrer</Bouton></div>}
      </div>
    </Panneau>
  );
}
